import clsx from 'clsx'
import { ArrowRight, ArrowUpRight, ChevronRight } from '@/assets/icons'

export type CardVariant = 'default' | 'alert-1' | 'alert-2' | 'alert-3' | 'upsell'
export type CardSize = 'default' | 'sm'
export type CardActionIcon = 'arrow-right' | 'arrow-up-right' | 'chevron-right'

const actionIcons = {
  'arrow-right': ArrowRight,
  'arrow-up-right': ArrowUpRight,
  'chevron-right': ChevronRight,
} satisfies Record<CardActionIcon, typeof ArrowRight>

export interface CardProps {
  /** Main title text */
  title: string
  /** Secondary text below the title. */
  description: React.ReactNode
  /** Click handler */
  onClick?: () => void
  /** Visual variant of the card. Defaults to 'default'. */
  variant?: CardVariant
  /** Size of the card. Defaults to 'default'. Use 'sm' for compact contexts like dropdown upsells. */
  size?: CardSize
  /** Optional icon rendered before the description text */
  descriptionIcon?: React.ReactNode
  /** Optional leading element rendered before the content (e.g. an icon badge) */
  leadingElement?: React.ReactNode
  /**
   * Optional image rendered in a fixed-width box with a divider before the content.
   */
  image?: React.ReactNode
  /** Action icon shown on the right. Defaults to 'arrow-up-right'. */
  actionIcon?: CardActionIcon
  /** Accessible label for the card button (e.g. "Open X" or "Go to Y") */
  'aria-label'?: string
  /** When true, the card is not clickable */
  disabled?: boolean
}

type VariantStyles = {
  container: string
  hover: string
  title: string
  description: string
  iconColor: string
  imageDivider: string
}

type SizeStyles = {
  borderRadius: string
  padding: string
  titleTypography: string
  descriptionTypography: string
  iconSize: string
  titleDescriptionGap: string
}

const variantStyles: Record<CardVariant, VariantStyles> = {
  default: {
    container: 'bg-surface-content border border-border-default',
    hover: 'hover-capable:hover:bg-surface-hover',
    title: 'text-text-primary',
    description: 'text-text-tertiary',
    iconColor: 'text-text-primary',
    imageDivider: 'border-border-default',
  },
  'alert-1': {
    container: 'bg-yellow-50 border border-border-default',
    hover: 'hover-capable:hover:bg-surface-hover',
    title: 'text-text-primary',
    description: 'text-yellow-800',
    iconColor: 'text-text-primary',
    imageDivider: 'border-border-default',
  },
  'alert-2': {
    container: 'bg-brand-50 border border-brand-200',
    hover: 'hover-capable:hover:bg-surface-brand-hover-strong',
    title: 'text-text-primary',
    description: 'text-text-tertiary',
    iconColor: 'text-text-primary',
    imageDivider: 'border-brand-200',
  },
  'alert-3': {
    container: 'bg-yellow-50 border border-border-default',
    hover: 'hover-capable:hover:bg-surface-hover',
    title: 'text-yellow-800',
    description: 'text-text-secondary',
    iconColor: 'text-text-primary',
    imageDivider: 'border-border-default',
  },
  upsell: {
    container: 'bg-brand-50 border border-brand-200',
    hover: 'hover-capable:hover:bg-surface-brand-hover-strong',
    title: 'text-brand-800',
    description: 'text-brand-800',
    iconColor: 'text-brand-800',
    imageDivider: 'border-brand-200',
  },
}

const sizeStyles: Record<CardSize, SizeStyles> = {
  default: {
    borderRadius: 'rounded-lg',
    padding: 'p-4',
    titleTypography: 'text-base font-rethink-sans font-semibold leading-7',
    descriptionTypography: 'text-sm font-medium leading-5',
    iconSize: 'w-6 h-6',
    titleDescriptionGap: '', // the title's leading-7 already spaces it from the description
  },
  sm: {
    borderRadius: 'rounded',
    padding: 'px-2.5 py-3',
    titleTypography: 'text-xs font-semibold',
    descriptionTypography: 'text-xs',
    iconSize: 'w-4 h-4',
    titleDescriptionGap: 'gap-1',
  },
}

export function Card({
  title,
  description,
  onClick,
  variant = 'default',
  size = 'default',
  descriptionIcon,
  leadingElement,
  image,
  actionIcon = 'arrow-up-right',
  'aria-label': ariaLabel,
  disabled = false,
}: CardProps) {
  const colors = variantStyles[variant]
  const sizes = sizeStyles[size]
  const ActionIcon = actionIcons[actionIcon]

  return (
    // div[role=button], not a native <button>, so `description` can carry its own interactive
    // content (e.g. a link) — a <button> can't legally contain nested interactive content.
    <div
      role="button"
      data-ds-component="Card"
      data-ds-variant={variant}
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-label={ariaLabel}
      onClick={disabled ? undefined : onClick}
      onKeyDown={
        disabled
          ? undefined
          : (event) => {
              // Ignore key events bubbling up from nested interactive content (e.g. a link in the description).
              if (event.target !== event.currentTarget) return
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onClick?.()
              }
            }
      }
      className={clsx(
        'group block h-full w-full text-left transition-colors',
        sizes.borderRadius,
        image ? 'overflow-hidden' : sizes.padding,
        'no-underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
        !disabled && 'cursor-pointer',
        disabled && 'cursor-not-allowed',
        !disabled && colors.hover,
        colors.container
      )}
    >
      <div className={clsx('flex', image ? 'items-stretch' : 'items-start gap-4')}>
        {image ? (
          <div className={clsx('flex w-[72px] shrink-0 items-center justify-center border-r px-2', colors.imageDivider)}>{image}</div>
        ) : (
          leadingElement && <div className="shrink-0 self-center">{leadingElement}</div>
        )}
        <div className={clsx('flex min-w-0 flex-1 flex-col', image && 'min-h-[80px] justify-center p-4', sizes.titleDescriptionGap)}>
          <div className="flex items-start justify-between gap-4">
            <p className={clsx('min-w-0 break-words', sizes.titleTypography, colors.title)}>{title}</p>
            <span className={clsx('shrink-0', disabled ? 'text-true-gray-300' : colors.iconColor)} aria-hidden>
              <ActionIcon className={sizes.iconSize} />
            </span>
          </div>
          {descriptionIcon ? (
            <div className="flex items-center gap-2">
              {descriptionIcon}
              <p className={clsx('min-w-0 break-words', sizes.descriptionTypography, colors.description)}>{description}</p>
            </div>
          ) : (
            <p className={clsx('min-w-0 break-words', sizes.descriptionTypography, colors.description)}>{description}</p>
          )}
        </div>
      </div>
    </div>
  )
}
